import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Ticket, Plus, Trash2, Loader2 } from "lucide-react";
import { ExportMenu } from "@/components/ExportMenu";
import { TableSkeleton } from "@/components/admin/AdminSkeletons";

export const CouponsManager = () => {
  const [coupons, setCoupons] = useState(null);
  const [form, setForm] = useState({ code: "", percent_off: "", active: true });
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/coupons").then((r) => setCoupons(r.data)).catch(() => setCoupons([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const pct = parseFloat(form.percent_off);
    if (!form.code.trim()) return toast.error("Enter a coupon code");
    if (!(pct > 0 && pct <= 90)) return toast.error("Discount must be 1–90%");
    setSaving(true);
    try {
      await api.post("/seller/coupons", { code: form.code.trim(), percent_off: pct, active: form.active });
      toast.success("Coupon created"); setForm({ code: "", percent_off: "", active: true }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const toggle = async (id) => { setBusy(id); try { await api.post(`/admin/coupons/${id}/toggle`); load(); } finally { setBusy(null); } };
  const del = async (id) => { if (!window.confirm("Delete this coupon?")) return; setBusy(id); try { await api.delete(`/admin/coupons/${id}`); toast.success("Coupon deleted"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(null); } };

  const cols = [
    { header: "Code", accessor: (c) => c.code },
    { header: "Discount %", accessor: (c) => c.percent_off },
    { header: "Scope", accessor: (c) => c.scope },
    { header: "Owner", accessor: (c) => c.owner },
    { header: "Active", accessor: (c) => (c.active ? "yes" : "no") },
  ];

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className="space-y-5">
      <Card className="p-5 shadow-sm" data-testid="coupon-create-card">
        <h3 className="font-semibold mb-4 flex items-center gap-2"><Ticket className="w-4 h-4 text-primary" />Create platform coupon</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-xs" htmlFor="cpn-code">Code</Label><Input id="cpn-code" data-testid="coupon-code-input" className="w-40 mt-1 uppercase" placeholder="WELCOME10" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} /></div>
          <div><Label className="text-xs" htmlFor="cpn-pct">Discount (%)</Label><Input id="cpn-pct" data-testid="coupon-percent-input" type="number" className="w-28 mt-1" value={form.percent_off} onChange={(e) => setForm({ ...form, percent_off: e.target.value })} /></div>
          <div className="flex items-center gap-2 pb-2"><Switch id="cpn-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="coupon-active-switch" aria-label="Coupon active" /><Label htmlFor="cpn-active" className="text-sm">Active</Label></div>
          <Button data-testid="create-coupon-btn" onClick={create} disabled={saving} className="rounded-full">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" />Create</>}</Button>
        </div>
      </Card>

      {coupons === null ? <TableSkeleton rows={5} cols={5} /> : (
        <Card className="p-0 overflow-hidden shadow-sm">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-semibold text-sm">All coupons <span className="text-muted-foreground font-normal">({coupons.length})</span></h3>
            <ExportMenu filename="coupons" title="Coupons" columns={cols} rows={coupons} testid="coupons-export" />
          </div>
          <Table>
            <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Discount</TableHead><TableHead>Scope / Owner</TableHead><TableHead>Active</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {coupons.map((c) => (
                <TableRow key={c.coupon_id} data-testid={`coupon-row-${c.coupon_id}`}>
                  <TableCell className="font-mono font-semibold">{c.code}</TableCell>
                  <TableCell>{c.percent_off}%</TableCell>
                  <TableCell><Badge variant="outline" className="capitalize mr-1">{c.scope}</Badge><span className="text-xs text-muted-foreground">{c.owner}</span></TableCell>
                  <TableCell>
                    <Switch checked={!!c.active} disabled={busy === c.coupon_id} onCheckedChange={() => toggle(c.coupon_id)} data-testid={`coupon-toggle-${c.coupon_id}`} aria-label={`Toggle ${c.code}`} />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" disabled={busy === c.coupon_id} onClick={() => del(c.coupon_id)} data-testid={`coupon-del-${c.coupon_id}`} aria-label={`Delete ${c.code}`}><Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {coupons.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground text-sm">No coupons yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Card>
      )}
    </motion.div>
  );
};
